import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, Mail, ShieldCheck, Sparkles } from 'lucide-react';
import CompanyLogo from '@/components/common/CompanyLogo';
import { authApi } from '@/lib/api/auth';
import { normalizeNationalMobileDigits } from '@/lib/auth/normalizeNationalMobile';
import { LoginFooterSvg } from './LoginFooterSvg';
import { LoginBrandFooter, LoginIconFloats } from './LoginPageChrome';

const RESEND_SECONDS = 90;
const OTP_LENGTH = 5;

const LoginMobileOtp: React.FC = () => {
  const [mobileRaw, setMobileRaw] = useState('');
  const [codeDigits, setCodeDigits] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [otpRequested, setOtpRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [cooldownLeft, setCooldownLeft] = useState(0);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string })?.from || '/';

  useEffect(() => {
    if (cooldownLeft <= 0) return;
    const id = window.setInterval(() => {
      setCooldownLeft((prev) => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    return () => window.clearInterval(id);
  }, [cooldownLeft]);

  const nationalTen = useMemo(() => normalizeNationalMobileDigits(mobileRaw), [mobileRaw]);
  const isMobileValid = nationalTen !== null;
  const mobileDisplay09 = nationalTen ? `0${nationalTen}` : '';
  const code = useMemo(() => codeDigits.join(''), [codeDigits]);
  const isCodeValid = code.length === OTP_LENGTH;
  const canResend = otpRequested && cooldownLeft === 0 && isMobileValid && !loading;

  const requestOtp = async () => {
    setError('');
    setOk('');
    setLoading(true);
    try {
      const res = await authApi.requestLoginOtp({ mobile_national: mobileRaw });
      setOtpRequested(true);
      setCooldownLeft(RESEND_SECONDS);
      setOk(res.message ?? 'کد تایید ارسال شد.');
      setCodeDigits(Array(OTP_LENGTH).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 30);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'ارسال کد ناموفق بود.');
    } finally {
      setLoading(false);
    }
  };

  const submitVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otpRequested) {
      await requestOtp();
      return;
    }
    setError('');
    setOk('');
    setLoading(true);
    try {
      await authApi.verifyLoginOtp({ mobile_national: mobileRaw, code });
      navigate(from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'کد نامعتبر است.');
    } finally {
      setLoading(false);
    }
  };

  const onDigitChange = (idx: number, value: string) => {
    const d = value.replace(/\D/g, '');
    if (!d) {
      setCodeDigits((prev) => {
        const next = [...prev];
        next[idx] = '';
        return next;
      });
      return;
    }
    const digit = d[d.length - 1];
    setCodeDigits((prev) => {
      const next = [...prev];
      next[idx] = digit;
      return next;
    });
    if (idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
  };

  const onDigitKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !codeDigits[idx] && idx > 0) {
      inputRefs.current[idx - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && idx < OTP_LENGTH - 1) inputRefs.current[idx + 1]?.focus();
    if (e.key === 'ArrowRight' && idx > 0) inputRefs.current[idx - 1]?.focus();
  };

  const onCodePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);
    if (!text) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill('');
    text.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setCodeDigits(next);
    const last = Math.min(text.length, OTP_LENGTH) - 1;
    inputRefs.current[Math.max(last, 0)]?.focus();
  };

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-100 via-emerald-50/95 to-green-100/85 px-3 pb-28 pt-6 sm:px-4 sm:pb-32 sm:pt-8"
      dir="rtl"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.38]"
        style={{
          backgroundImage: `
            linear-gradient(to right, rgb(10 77 41 / 0.055) 1px, transparent 1px),
            linear-gradient(to bottom, rgb(10 77 41 / 0.055) 1px, transparent 1px)
          `,
          backgroundSize: '32px 32px',
        }}
      />

      <LoginIconFloats />
      <LoginFooterSvg />
      <LoginBrandFooter />

      <div className="relative z-10 w-full max-w-md animate-card-rise">
        <div className="relative rounded-2xl border border-emerald-200/70 bg-white/92 p-6 sm:p-8 shadow-2xl shadow-emerald-950/10 backdrop-blur-sm ring-1 ring-emerald-100/90">
          <div
            className="pointer-events-none absolute -inset-px rounded-2xl opacity-55"
            style={{
              background:
                'linear-gradient(135deg, rgba(16, 120, 62, 0.1) 0%, transparent 42%, rgba(10, 77, 41, 0.05) 100%)',
            }}
          />

          <div className="relative text-center mb-8">
            {otpRequested ? (
              <div className="inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-600 to-[#0a4d29] text-white shadow-lg animate-icon-glow-green">
                <ShieldCheck className="h-9 w-9" strokeWidth={1.75} />
              </div>
            ) : (
              <CompanyLogo
                className="inline-flex h-[4.5rem] w-[4.5rem] items-center justify-center rounded-2xl bg-white p-1.5 shadow-lg ring-1 ring-emerald-200/80 animate-icon-glow-green"
                imgClassName="h-full w-full object-contain"
              />
            )}
            <h1 className="mt-5 text-2xl font-bold tracking-tight text-slate-800">
              {otpRequested ? 'تایید کد پیامکی' : 'ورود با شماره موبایل'}
            </h1>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">
              {otpRequested ? 'کد ۵ رقمی را وارد کنید.' : 'شماره ثبت‌شده در پروفایل کاربر را وارد کنید.'}
            </p>
            <div className="mt-4 flex items-center justify-center gap-2 text-xs text-emerald-900/75">
              <Sparkles size={14} className="shrink-0 text-emerald-600" />
              <span>آنالیز بها — ورود بدون رمز عبور</span>
            </div>
          </div>

          <form onSubmit={submitVerify} className="relative space-y-4">
            {!otpRequested && (
              <div>
                <label className="mb-1 block text-sm font-medium text-slate-700">شماره موبایل</label>
                <div
                  dir="ltr"
                  className="flex items-center rounded-xl border border-slate-200/90 bg-white/85 px-3 shadow-sm transition focus-within:border-emerald-600 focus-within:ring-2 focus-within:ring-emerald-600/25"
                >
                  <span className="text-sm text-slate-500 mr-2">+98</span>
                  <input
                    type="text"
                    value={mobileRaw}
                    onChange={(e) => setMobileRaw(e.target.value.replace(/\D/g, '').slice(0, 14))}
                    className="w-full bg-transparent px-2 py-2.5 text-left text-slate-800 outline-none"
                    placeholder="9123456789"
                    inputMode="numeric"
                    autoComplete="tel-national"
                  />
                </div>
                <p className="mt-1.5 text-xs text-slate-500 leading-relaxed">
                  فرمت‌های ۰۹۱۲…، ۹۱۲… یا ۹۸۹… پذیرفته می‌شود. شماره باید در پروفایل کاربر ثبت باشد.
                </p>
                {mobileRaw.length > 0 && !isMobileValid && (
                  <p className="mt-1 text-xs text-amber-700">الگوی شماره کامل نیست.</p>
                )}
              </div>
            )}

            {!otpRequested ? (
              <button
                type="button"
                onClick={requestOtp}
                disabled={!isMobileValid || loading}
                className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-emerald-800 to-[#0a4d29] py-2.5 font-medium text-white shadow-md transition hover:from-emerald-900 hover:to-emerald-950 focus:outline-none focus:ring-2 focus:ring-emerald-600/45 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <span
                  className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                  aria-hidden
                />
                <span className="relative inline-flex w-full items-center justify-center gap-2">
                  {loading ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      در حال ارسال...
                    </>
                  ) : (
                    <>
                      <Mail className="h-5 w-5" />
                      ارسال کد تایید
                    </>
                  )}
                </span>
              </button>
            ) : (
              <>
                {mobileDisplay09 ? (
                  <p className="text-center text-sm text-slate-600">
                    <span className="text-slate-500">ارسال به: </span>
                    <span dir="ltr" className="font-mono font-semibold text-slate-900">
                      {mobileDisplay09}
                    </span>
                  </p>
                ) : null}
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">کد تایید</label>
                  <div className="grid grid-cols-5 gap-2" dir="ltr">
                    {codeDigits.map((digit, idx) => (
                      <input
                        key={idx}
                        ref={(el) => {
                          inputRefs.current[idx] = el;
                        }}
                        type="text"
                        value={digit}
                        onChange={(e) => onDigitChange(idx, e.target.value)}
                        onKeyDown={(e) => onDigitKeyDown(idx, e)}
                        onPaste={onCodePaste}
                        className="h-12 rounded-xl border border-slate-200/90 bg-white/85 text-center text-lg font-bold text-slate-800 shadow-sm outline-none transition focus:border-emerald-600 focus:ring-2 focus:ring-emerald-600/25"
                        inputMode="numeric"
                        maxLength={1}
                        autoComplete="one-time-code"
                      />
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={loading || !canResend}
                  className="w-full rounded-xl border border-emerald-200 bg-emerald-50/80 py-2.5 text-sm font-medium text-emerald-900 transition hover:bg-emerald-100/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {loading ? '...' : cooldownLeft > 0 ? `ارسال مجدد تا ${cooldownLeft} ثانیه` : 'ارسال مجدد کد'}
                </button>
                <button
                  type="submit"
                  disabled={loading || !isCodeValid}
                  className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-l from-emerald-800 to-[#0a4d29] py-2.5 font-medium text-white shadow-md transition hover:from-emerald-900 hover:to-emerald-950 focus:outline-none focus:ring-2 focus:ring-emerald-600/45 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <span
                    className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/20 to-transparent transition-transform duration-700 group-hover:translate-x-full"
                    aria-hidden
                  />
                  <span className="relative inline-flex w-full items-center justify-center gap-2">
                    {loading ? (
                      <>
                        <Loader2 className="h-5 w-5 animate-spin" />
                        در حال ورود...
                      </>
                    ) : (
                      <>
                        <ShieldCheck className="h-5 w-5" />
                        تایید و ورود
                      </>
                    )}
                  </span>
                </button>
              </>
            )}

            {ok && (
              <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/90 px-3 py-2.5 text-center text-sm text-emerald-800">
                {ok}
              </div>
            )}
            {error && (
              <div className="rounded-xl border border-red-200/80 bg-red-50/90 px-3 py-2.5 text-center text-sm text-red-800">
                {error}
              </div>
            )}

            <button
              type="button"
              onClick={() => navigate('/login', { replace: true, state: { from } })}
              className="flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50 hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              بازگشت به ورود با نام کاربری
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginMobileOtp;
